import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Box,
    createStyles,
    Grid,
    makeStyles,
    TextField,
    Theme,
    Typography
} from "@material-ui/core";
import { Add } from '@material-ui/icons';
import { connect, useStore } from "react-redux";
import { Store } from "redux";
import { useSnackbar } from 'notistack';
import { ApplicationState } from "../../redux/reducers";
import { avanzarSeccionGuiaWizard, IGuiaWizard } from "../../entities/guias/IGuiaWizard";
import { Mercancia } from "../../entities/solicitudes/Mercancia";
import { MercanciaDatosComplementarios } from "../../entities/solicitudes/MercanciaDatosComplementarios";
import {
    setGuiaMercancias,
    setGuiaMercanciasComplementarias,
    setGuiaWizard
} from "../../redux/actions/guiaActions";
import { loadEmbalajes } from "../../redux/actions/embalajeActions";
import MercanciaListaGuia from "./MercanciaListaGuia";
import MercanciaDatosComplementariosListaGuia from "./MercanciaDatosComplementariosListaGuia";
import Help from "../help/Help";
import useCommonApi from "../../api/useCommonApi";
import { traducirButton, traducirLabel, traducirMessage } from "../../blocks/traductor";
import swal from 'sweetalert2';
import { ICliente } from "../../entities/catalogos/ICliente";
import { IRecoleccion } from "../../redux/types/recoleccionActionTypes";
import {
    debeMostrarDatosComplementariosGuia,
    esCartaPorteDetalleActivo,
    obtenerClasificacionDesdeAreaServicio,
    obtenerCodigoPostalRecoleccionGuia
} from "./guiaMercanciaComplementariosHelpers";
import SeguroGuia from "./SeguroGuia";
import ReferenciasGuia from "./ReferenciasGuia";
import { IGuiaSeguroState } from "./guiaSeguroHelpers";
import { IGuiaClienteReferencia } from "../../entities/guias/IGuiaClienteReferencia";
import { IParametro } from "../../redux/types/parametroActionTypes";
import { ICatalogo } from "../../entities/catalogos/ICatalogo";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: '100%',
            boxShadow: "none",
            padding: theme.spacing(2)
        },
        heading: {
            fontSize: "0.875rem",
            color: "#00653b",
            fontWeight: theme.typography.fontWeightBold,
            borderBottom: "2px solid #00653b"
        }
    }),
);

const hayPartidaMercanciaValida = (mercancias: Mercancia[]) =>
    mercancias.some(m =>
        Number(m.cantidad) > 0
        && Number(m.embalajeId) > 0
        && Number(m.alto) > 0
        && Number(m.largo) > 0
        && Number(m.ancho) > 0
        && Number(m.peso) > 0
        && Number(m.volumen) > 0
    );

const leerUnidadesCliente = (): any[] => {
    try {
        const unidades = JSON.parse(localStorage.getItem("unidades") || "[]");
        return Array.isArray(unidades) ? unidades : [];
    } catch {
        return [];
    }
};

const unidadesGuiaSinDefinir = (wizard: IGuiaWizard): boolean => {
    const peso = wizard.unidadMedidaPesoId;
    const longitud = wizard.unidadMedidaLongitudId;
    return (peso === null || peso === undefined || peso === 0)
        && (longitud === null || longitud === undefined || longitud === 0);
};

const resolverDefaultsUnidadesCliente = (): Pick<IGuiaWizard, 'unidadMedidaPesoId' | 'unidadMedidaLongitudId'> | null => {
    const vUnidades = leerUnidadesCliente();
    if (!vUnidades.length) {
        return null;
    }

    const pesoUnidades = vUnidades.filter(u => u.tipo_unidad_nombre?.toUpperCase() === "PESO");
    const medidaUnidades = vUnidades.filter(u => u.tipo_unidad_nombre?.toUpperCase() === "MEDIDAS");
    const primerPesoId = pesoUnidades.length > 0 ? pesoUnidades[0].unidad_id : 0;
    const primerMedidaId = medidaUnidades.length > 0 ? medidaUnidades[0].unidad_id : 0;

    if (!primerPesoId || !primerMedidaId) {
        return null;
    }

    return {
        unidadMedidaPesoId: primerPesoId,
        unidadMedidaLongitudId: primerMedidaId
    };
};

interface StateProps {
    guiaWizard: IGuiaWizard;
    guiaMercancias: Mercancia[];
    guiaMercanciasComplementarias: MercanciaDatosComplementarios[];
    guiaOrigen: IRecoleccion;
    guiaDestino: IRecoleccion;
    cliente: ICliente;
    parametros: IParametro[];
    tiposSeguroObservacion: ICatalogo[];
}

interface DispatchProps {
    setGuiaWizard: typeof setGuiaWizard;
    setGuiaMercancias: typeof setGuiaMercancias;
    setGuiaMercanciasComplementarias: typeof setGuiaMercanciasComplementarias;
    loadEmbalajes: typeof loadEmbalajes;
}

type Props = StateProps & DispatchProps;

const DatosMercanciaGuia = ({
    guiaWizard,
    guiaMercancias,
    guiaMercanciasComplementarias,
    guiaOrigen,
    guiaDestino,
    cliente,
    parametros,
    tiposSeguroObservacion,
    setGuiaWizard,
    setGuiaMercancias,
    setGuiaMercanciasComplementarias,
    loadEmbalajes
}: Props) => {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const apiSource = useCommonApi();
    const store = useStore() as Store<ApplicationState>;
    const excluirSeguroInitRef = useRef(false);

    const descripcionCompleta = !!(guiaWizard.descripcionMercancia?.trim());

    const [clasificacionOrigenId, setClasificacionOrigenId] = useState<number | undefined>();
    const [clasificacionDestinoId, setClasificacionDestinoId] = useState<number | undefined>();
    const [clasificacionesResueltas, setClasificacionesResueltas] = useState(false);

    const [complementariosHabilitado, setComplementariosHabilitado] = useState(
        () => guiaMercanciasComplementarias.length > 0
    );
    const [descripcionContenidoHabilitado, setDescripcionContenidoHabilitado] = useState(
        () => hayPartidaMercanciaValida(guiaMercancias)
            && (!guiaMercanciasComplementarias.length || descripcionCompleta)
            || descripcionCompleta
    );
    const [seguroHabilitado, setSeguroHabilitado] = useState(() => descripcionCompleta);
    const [referenciasHabilitado, setReferenciasHabilitado] = useState(
        () => guiaWizard.referenciasCatalogoCargado
    );
    const [expanded, setExpanded] = useState<string | false>(() => {
        if (guiaWizard.referenciasCatalogoCargado) {
            return 'Referencias';
        }
        if (descripcionCompleta) {
            return 'Seguro';
        }
        if (guiaWizard.descripcionMercancia?.trim()) {
            return 'DescripcionContenido';
        }
        if (guiaMercanciasComplementarias.length > 0) {
            return 'DetalleMercanciaComplementaria';
        }
        return 'DetalleMercancia';
    });

    const cartaPorteDetalleActivo = esCartaPorteDetalleActivo(cliente);

    const mostrarComplementarios = useMemo(() => {
        if (!clasificacionesResueltas) {
            return false;
        }
        return debeMostrarDatosComplementariosGuia(
            cartaPorteDetalleActivo,
            cliente,
            clasificacionOrigenId,
            clasificacionDestinoId
        );
    }, [clasificacionesResueltas, clasificacionOrigenId, clasificacionDestinoId, cartaPorteDetalleActivo, cliente]);

    useEffect(() => {
        loadEmbalajes();
    }, []);

    useEffect(() => {
        if (excluirSeguroInitRef.current || !cliente.esExcluirSeguro) {
            return;
        }
        excluirSeguroInitRef.current = true;
        const wizardActual = store.getState().guiaWizard;
        setGuiaWizard({
            ...wizardActual,
            esSeguroBasico: false,
            esSeguroMercancia: false,
            valorMercancia: null
        });
    }, [cliente.esExcluirSeguro, setGuiaWizard, store]);

    useEffect(() => {
        if (!unidadesGuiaSinDefinir(guiaWizard)) {
            return;
        }

        const defaultsUnidades = resolverDefaultsUnidadesCliente();
        if (!defaultsUnidades) {
            return;
        }

        const wizardActual = store.getState().guiaWizard;
        if (!unidadesGuiaSinDefinir(wizardActual)) {
            return;
        }

        setGuiaWizard({
            ...wizardActual,
            unidadMedidaPesoId: defaultsUnidades.unidadMedidaPesoId,
            unidadMedidaLongitudId: defaultsUnidades.unidadMedidaLongitudId
        });
    }, [guiaWizard.unidadMedidaPesoId, guiaWizard.unidadMedidaLongitudId, setGuiaWizard, store]);

    useEffect(() => {
        let cancelled = false;

        const resolverClasificacion = async (
            recoleccion: IRecoleccion
        ): Promise<number | undefined> => {
            const desdeArea = obtenerClasificacionDesdeAreaServicio(recoleccion.areaServicio);
            if (desdeArea !== undefined) {
                return desdeArea;
            }
            const cp = obtenerCodigoPostalRecoleccionGuia(recoleccion);
            if (!cp) {
                return undefined;
            }
            const result = await apiSource.obtenerAreaServicioPorCpAsync<{ clasificacionID?: number }>(
                "area-servicio/by-cp",
                { cp }
            );
            if (result?.clasificacionID != null) {
                return Number(result.clasificacionID);
            }
            return undefined;
        };

        (async () => {
            const origenId = await resolverClasificacion(guiaOrigen);
            const destinoId = await resolverClasificacion(guiaDestino);
            if (cancelled) {
                return;
            }
            setClasificacionOrigenId(origenId);
            setClasificacionDestinoId(destinoId);
            setClasificacionesResueltas(true);
        })();

        return () => {
            cancelled = true;
        };
    }, [guiaOrigen, guiaDestino, apiSource]);

    useEffect(() => {
        if (!clasificacionesResueltas) {
            return;
        }
        if (!mostrarComplementarios) {
            setGuiaMercanciasComplementarias([]);
            setComplementariosHabilitado(false);
        }
    }, [clasificacionesResueltas, mostrarComplementarios, setGuiaMercanciasComplementarias]);

    useEffect(() => {
        if (hayPartidaMercanciaValida(guiaMercancias) || guiaWizard.descripcionMercancia?.trim()) {
            if (!mostrarComplementarios || guiaMercanciasComplementarias.length > 0) {
                setDescripcionContenidoHabilitado(true);
            }
        }
        if (guiaMercanciasComplementarias.length > 0) {
            setComplementariosHabilitado(true);
        }
    }, [guiaMercancias, guiaWizard.descripcionMercancia, guiaMercanciasComplementarias, mostrarComplementarios]);

    const handleChangePanel = (panel: string) => (_event: React.ChangeEvent<{}>, isExpanded: boolean) => {
        if (panel === 'DetalleMercanciaComplementaria' && !complementariosHabilitado) {
            return;
        }
        if (panel === 'DescripcionContenido' && !descripcionContenidoHabilitado) {
            return;
        }
        if (panel === 'Seguro' && !seguroHabilitado) {
            return;
        }
        if (panel === 'Referencias' && !referenciasHabilitado) {
            return;
        }
        setExpanded(isExpanded ? panel : false);
    };

    const handleDetalleCompleto = (mercancias: Mercancia[]) => {
        if (!clasificacionesResueltas) {
            enqueueSnackbar(traducirLabel(240), { variant: "info" });
            return;
        }

        if (!hayPartidaMercanciaValida(mercancias)) {
            swal.fire({
                titleText: traducirMessage(11),
                text: traducirMessage(132),
                type: 'error',
            });
            return;
        }

        setGuiaMercancias(mercancias.map(m => new Mercancia(m)));

        if (mostrarComplementarios) {
            setComplementariosHabilitado(true);
            setExpanded('DetalleMercanciaComplementaria');
            return;
        }

        setGuiaMercanciasComplementarias([]);
        setDescripcionContenidoHabilitado(true);
        setExpanded('DescripcionContenido');
    };

    const handleComplementariosCompleto = (mercanciasComplementarias: MercanciaDatosComplementarios[]) => {
        setGuiaMercanciasComplementarias(mercanciasComplementarias.map(m => new MercanciaDatosComplementarios(m)));
        setDescripcionContenidoHabilitado(true);
        setExpanded('DescripcionContenido');
    };

    const handleDescripcionMercancia = (value: string) => {
        setGuiaWizard({
            ...guiaWizard,
            descripcionMercancia: value
        });
    };

    const handleDescripcionSiguiente = () => {
        const descripcion = (guiaWizard.descripcionMercancia ?? '').trim();
        if (!descripcion) {
            enqueueSnackbar(traducirMessage(107), { variant: "error" });
            return;
        }

        setGuiaWizard({
            ...guiaWizard,
            descripcionMercancia: descripcion
        });
        setSeguroHabilitado(true);
        setExpanded('Seguro');
    };

    const handleSeguroChange = (patch: Partial<IGuiaSeguroState>) => {
        setGuiaWizard({
            ...guiaWizard,
            ...patch
        });
    };

    const handleSeguroAvanzar = () => {
        setReferenciasHabilitado(true);
        setExpanded('Referencias');
    };

    const handleReferenciasCatalogoInicializado = (referencias: IGuiaClienteReferencia[]) => {
        setGuiaWizard({
            ...guiaWizard,
            clienteReferencias: referencias,
            referenciasCatalogoCargado: true
        });
    };

    const handleReferenciasAvanzar = (referencias: IGuiaClienteReferencia[]) => {
        const wizardActual = store.getState().guiaWizard;
        setGuiaWizard(avanzarSeccionGuiaWizard({
            ...wizardActual,
            clienteReferencias: referencias,
            referenciasCatalogoCargado: true
        }, 3));
    };

    const seguroState: IGuiaSeguroState = {
        esSeguroBasico: guiaWizard.esSeguroBasico,
        esSeguroMercancia: guiaWizard.esSeguroMercancia,
        valorMercancia: guiaWizard.valorMercancia
    };

    const descripcionLength = guiaWizard.descripcionMercancia?.length || 0;

    return (
        <div className={classes.root}>
            <Accordion
                expanded={expanded === 'DetalleMercancia'}
                onChange={handleChangePanel('DetalleMercancia')}
            >
                <AccordionSummary
                    expandIcon={<Add />}
                    aria-controls="mercancia-guia-content"
                    id="mercancia-guia-header"
                >
                    <Typography component="span" className={classes.heading}>
                        {traducirLabel(88)}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={1}>
                        <Grid item sm={12}>
                            <MercanciaListaGuia
                                value={guiaMercancias}
                                siguienteTab={handleDetalleCompleto}
                            />
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>

            {mostrarComplementarios &&
                <Accordion
                    expanded={expanded === 'DetalleMercanciaComplementaria'}
                    onChange={handleChangePanel('DetalleMercanciaComplementaria')}
                >
                    <AccordionSummary
                        disabled={!complementariosHabilitado}
                        expandIcon={<Add />}
                        aria-controls="complementarios-guia-content"
                        id="complementarios-guia-header"
                    >
                        <Typography component="span" className={classes.heading}>
                            {traducirLabel(89)}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Grid container spacing={1}>
                            <Grid item sm={12}>
                                <Box>
                                    <Help comments={traducirMessage(41)} customStyle={{ top: "-30px", right: "-20px" }} />
                                </Box>
                                <MercanciaDatosComplementariosListaGuia
                                    value={guiaMercanciasComplementarias}
                                    siguienteTab={handleComplementariosCompleto}
                                    clasificacionOrigenId={clasificacionOrigenId}
                                    clasificacionDestinoId={clasificacionDestinoId}
                                />
                            </Grid>
                        </Grid>
                    </AccordionDetails>
                </Accordion>
            }

            <Accordion
                expanded={expanded === 'DescripcionContenido'}
                onChange={handleChangePanel('DescripcionContenido')}
            >
                <AccordionSummary
                    disabled={!descripcionContenidoHabilitado}
                    expandIcon={<Add />}
                    aria-controls="descripcion-contenido-guia-content"
                    id="descripcion-contenido-guia-header"
                >
                    <Typography component="span" className={classes.heading}>
                        {traducirLabel(83)}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={1}>
                        <Grid item sm={12}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item sm={12}>
                                    <Box mb={3} className="title text-center">
                                        {traducirLabel(83)}
                                    </Box>
                                </Grid>
                                <Grid item sm={12}>
                                    <TextField
                                        fullWidth
                                        id="guia-descripcion-contenido"
                                        variant="outlined"
                                        required={true}
                                        title={traducirMessage(298)}
                                        onChange={e => handleDescripcionMercancia(e.target.value)}
                                        rows={10}
                                        value={guiaWizard.descripcionMercancia ?? ""}
                                        multiline
                                        inputProps={{ maxLength: 250 }}
                                    />
                                    <Typography
                                        variant="body2"
                                        style={{
                                            color: descripcionLength >= 250 ? 'red' : '#333',
                                            fontWeight: 'bold',
                                            display: 'block',
                                            textAlign: 'right',
                                            marginTop: '4px'
                                        }}
                                    >
                                        {descripcionLength} / 250
                                    </Typography>
                                </Grid>
                                <Grid item sm={12} style={{ textAlign: "end" }}>
                                    <Button variant="contained" color="primary" onClick={handleDescripcionSiguiente}>
                                        {traducirButton(15)}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>

            <Accordion
                expanded={expanded === 'Seguro'}
                onChange={handleChangePanel('Seguro')}
            >
                <AccordionSummary
                    disabled={!seguroHabilitado}
                    expandIcon={<Add />}
                    aria-controls="seguro-guia-content"
                    id="seguro-guia-header"
                >
                    <Typography component="span" className={classes.heading}>
                        {traducirLabel(272)}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={1}>
                        <Grid item sm={12}>
                            <SeguroGuia
                                seguro={seguroState}
                                onSeguroChange={handleSeguroChange}
                                onAvanzar={handleSeguroAvanzar}
                                cliente={cliente}
                                parametros={parametros}
                                tiposSeguroObservacion={tiposSeguroObservacion}
                            />
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>

            <Accordion
                expanded={expanded === 'Referencias'}
                onChange={handleChangePanel('Referencias')}
            >
                <AccordionSummary
                    disabled={!referenciasHabilitado}
                    expandIcon={<Add />}
                    aria-controls="referencias-guia-content"
                    id="referencias-guia-header"
                >
                    <Typography component="span" className={classes.heading}>
                        {traducirLabel(260)}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={1}>
                        <Grid item sm={12}>
                            <Box>
                                <Help comments={traducirMessage(35)} customStyle={{ bottom: "10px", right: "-15px" }} />
                            </Box>
                            <Box mb={3} className="title text-center">
                                {traducirMessage(21)}
                            </Box>
                            <ReferenciasGuia
                                referencias={guiaWizard.clienteReferencias}
                                referenciasCatalogoCargado={guiaWizard.referenciasCatalogoCargado}
                                clienteId={cliente.id}
                                onCatalogoInicializado={handleReferenciasCatalogoInicializado}
                                onAvanzar={handleReferenciasAvanzar}
                            />
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>

        </div>
    );
};

const mapStateToProps = (state: ApplicationState) => ({
    guiaWizard: state.guiaWizard,
    guiaMercancias: state.guiaMercancias,
    guiaMercanciasComplementarias: state.guiaMercanciasComplementarias,
    guiaOrigen: state.guiaOrigen,
    guiaDestino: state.guiaDestino,
    cliente: state.cliente,
    parametros: state.parametros,
    tiposSeguroObservacion: state.tiposSeguroObservacion
});

const mapDispatchToProps = {
    setGuiaWizard,
    setGuiaMercancias,
    setGuiaMercanciasComplementarias,
    loadEmbalajes
};

export default connect(mapStateToProps, mapDispatchToProps)(DatosMercanciaGuia);
